import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { OrganizationsAPI } from '@saraa/organizations/api/OrganizationsAPI.js';
import { html } from 'lit';
import { dialogHeader } from './ui.js';

const PickOrganizationDialog = stateful(
  (props: {
    organizations: Array<{ _id: string; name?: string | null }>;
    resolve: (organizationId: string) => void;
    reject: () => void;
  }) => {
    const [selected, setSelected] = useState(props.organizations[0]?._id ?? '');
    const nameById = new Map(props.organizations.map(organization => [organization._id, organization.name ?? '']));

    return html`
      <div class="flex flex-col gap-4">
        ${dialogHeader({
          title: 'Which organization?',
          description:
            'You administer more than one, so pick the one this should belong to. It cannot be changed afterwards.',
        })}
        ${DropdownSelectionInput<string>({
          value: selected,
          onPicked: value => setSelected(value ?? ''),
          selectables: props.organizations.map(organization => organization._id),
          renderString: id => nameById.get(id) || id,
        })}

        <div class="flex justify-end gap-2">
          <button class="btn btn-ghost" @click=${props.reject}>Cancel</button>
          <button class="btn btn-primary" ?disabled=${!selected} @click=${() => props.resolve(selected)}>
            Continue
          </button>
        </div>
      </div>
    `;
  },
);

/**
 * The organization a new template or template field will belong to.
 *
 * Ownership is fixed at creation — it is what every later permission check reads
 * and no endpoint will move it — so it has to be settled before the entity
 * exists rather than left to the form. With a single organization there is
 * nothing to ask.
 *
 * The list comes from the server, so what is offered here is already only what
 * the caller may file work under; the create endpoint checks it again anyway.
 */
export const pickOrganizationId = async (): Promise<string> => {
  const organizations = await OrganizationsAPI.getMine()({ _id: 1, name: 1 });

  if (!organizations.length) {
    throw new ExpectedError('You do not belong to any organization, so there is nothing to create this under.');
  }

  const only = organizations[0];
  if (organizations.length === 1 && only) return only._id;

  return showDialog<string>({
    content: (resolve, reject) =>
      PickOrganizationDialog({
        organizations: organizations.map(organization => ({ _id: organization._id, name: organization.name })),
        resolve,
        reject,
      }),
  });
};
